import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import type { FetchQueryOptions } from '@tanstack/react-query';
import api from '../api/config';
import { IAM_ME_KEY } from '../hooks/queryKeys';
import { fetchMyIamPermissions } from '../services/iam';
import { toReleaseMap, type ReleaseMap } from '../components/whats-new/seenRelease';
import { clearSentryUser, setSentryUser } from '@odo/shared-ui/observability/sentry';
import { patientPagePrefetches } from './bootPrefetch';
import { queryClient } from './queryClient';

interface UserData {
    id: string;
    isAdmin: boolean;
    practices: string[];
    lastSeenRelease?: string | null;
    lastSeenReleases: ReleaseMap;
}

interface User {
    id: string;
    email: string;
    name: string;
    profilePictureUrl?: string | null;
    database: {
        data: UserData;
    };
}

type VerifiedUser = Omit<User, 'database'> & {
    database: {
        data: Omit<UserData, 'lastSeenReleases'> & { lastSeenReleases?: ReleaseMap | null };
    };
};

function normalizeUser(raw: VerifiedUser): User {
    return {
        ...raw,
        database: {
            ...raw.database,
            data: {
                ...raw.database.data,
                lastSeenReleases: toReleaseMap(raw.database.data.lastSeenReleases)
            }
        }
    };
}

interface UserContextType {
    user: User | null;
    isLoading: boolean;
    isAuthenticated: boolean;
    login: (userData: VerifiedUser) => void;
    logout: () => void;
    setProfilePictureUrl: (url: string) => void;
    setName: (name: string) => void;
    updateLastSeenReleases: (update: (current: ReleaseMap) => ReleaseMap) => void;
}

function withLastSeenReleases(user: User, update: (current: ReleaseMap) => ReleaseMap): User {
    const data = user.database.data;
    return {
        ...user,
        database: {
            ...user.database,
            data: { ...data, lastSeenReleases: update(data.lastSeenReleases) }
        }
    };
}

const UserContext = createContext<UserContextType | undefined>(undefined);

interface UserProviderProps {
    children: ReactNode;
}

function UserProvider({ children }: UserProviderProps) {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        const checkAuth = async () => {
            const token = localStorage.getItem('odo_login_token');
            if (!token) {
                setIsLoading(false);
                return;
            }

            // These need only the stored token, so they ride the verify wave.
            const bootQueries: FetchQueryOptions[] = [
                { queryKey: IAM_ME_KEY, queryFn: fetchMyIamPermissions },
                ...patientPagePrefetches(window.location.pathname)
            ];
            for (const query of bootQueries) {
                void queryClient.prefetchQuery(query);
            }
            try {
                const response = await api.get('/user/verify');
                if ([200, 201].includes(response.status)) {
                    const userData: VerifiedUser = response.data.user;
                    if (!userData.database.data.isAdmin) {
                        localStorage.removeItem('odo_login_token');
                        setUser(null);
                        window.location.href = '/forbidden';
                        return;
                    }
                    setSentryUser(userData);
                    setUser(normalizeUser(userData));
                }
            } catch {
                localStorage.removeItem('odo_login_token');
                // Fetched under the rejected token; the next login must not reuse them.
                for (const { queryKey } of bootQueries) {
                    queryClient.removeQueries({ queryKey, exact: true });
                }
                setUser(null);
            } finally {
                setIsLoading(false);
            }
        };

        checkAuth();
    }, []);

    const login = useCallback((userData: VerifiedUser) => {
        setSentryUser(userData);
        setUser(normalizeUser(userData));
    }, []);

    const logout = useCallback(() => {
        clearSentryUser();
        setUser(null);
        localStorage.removeItem('odo_login_token');
        queryClient.clear();
    }, []);

    const setProfilePictureUrl = useCallback(
        (url: string) => {
            if (user) {
                setUser({ ...user, profilePictureUrl: url });
            }
        },
        [user]
    );

    const setName = useCallback(
        (name: string) => {
            if (user) {
                setUser({ ...user, name });
            }
        },
        [user]
    );

    const updateLastSeenReleases = useCallback((update: (current: ReleaseMap) => ReleaseMap) => {
        setUser((prev) => (prev ? withLastSeenReleases(prev, update) : prev));
    }, []);

    const value: UserContextType = {
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        logout,
        setProfilePictureUrl,
        setName,
        updateLastSeenReleases
    };

    return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
}

export { UserProvider };

function useUser(): UserContextType {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
}

export { useUser };
